import React, {
  createContext,
  useState,
  useMemo,
  useEffect,
  useContext,
  useCallback,
} from 'react';
import { toast } from 'react-toastify';
import { useLocation } from 'react-router-dom';
import { useForm, useFieldArray, useWatch } from 'react-hook-form';
import { useQuery, useMutation } from '@tanstack/react-query';
import { yupResolver } from '@hookform/resolvers/yup';
import { fetchExpenseClaimDetails } from '../../../../store/apis/expenseService';
import {
  validationSchema,
  defaultValues,
  modalFieldInitialValues,
  initialValues,
  processClaimSortConfig,
  processClaimModalSortConfig,
  rowSchema,
} from '../ProcessExpenseClaim/constants';
import { DateUtil, TableUtil } from '../../../../utils';
import { useExpenseClaimUtilCtx } from '../../context/ExpenseClaimUtilProvider';
import { useTableFEHook } from '../../../../components/hooks';

const ProcessExpenseClaimContext = createContext();

export function useProcessExpenseClaimCtx() {
  const ctx = useContext(ProcessExpenseClaimContext);
  if (!ctx) {
    throw new Error(
      'useProcessExpenseClaimCtx must be used within ProcessExpenseClaimProvider',
    );
  }
  return ctx;
}

export function ProcessExpenseClaimProvider({ children }) {
  const {
    defaultCurrCode,
    expenseCategories,
    fetchCodeListMutation,
    fetchDefaultExpenseClaimData,
  } = useExpenseClaimUtilCtx();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const claimId = queryParams.get('ID');
  const thisYear = new Date().getFullYear();
  const thisMonth = new Date().getMonth();
  const { endDate } = DateUtil.getMonthRange(thisMonth, thisYear);
  const [expenseClaimHeaders, setExpenseClaimHeaders] = useState({
    claimNo: '',
    claimDate: DateUtil.formatDateObjToHtmlDate(new Date(endDate)),
    currency: 'AED',
    claimAmt: 0,
    reimburAmt: 0,
    advanceRecovery: 0,
    comments: '',
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalConfig, setModalConfig] = useState({
    title: 'Modal Title',
    dataConfig: null,
    data: [],
  });
  const [viewAttachmentsModalOpen, setViewAttachmentsModalOpen] =
    useState(false);
  const [viewAttachmentSrNo, setViewAttachmentSrNo] = useState(null);
  const [oldResult, setOldResult] = useState([]);
  const [expenseCatMapping, setExpenseCatMapping] = useState({});
  const [expenseTypeMapping, setExpenseTypeMapping] = useState({});
  const [sortFilterState, setSortFilterState] = useState(false);
  const [defaultExpenseData, setDefaultExpenseData] = useState(null);

  const {
    pageRequest,
    setPageRequest,
    applyFilter,
    handleManualSort,
    resetFilters,
    checkColumnIfSorted,
    columnName,
    setColumnName,
    clearSingleFilterValue,
    handleAutoSort,
  } = useTableFEHook({
    defaultPageRequest: {
      sortColumn: '',
      sortDirection: '',
      filters: [],
    },
  });

  const getExpenseClaimDetailsMutation = useMutation({
    mutationFn: payload => fetchExpenseClaimDetails(payload),
  });

  const { data: expenseClaimDetailsHeader, isLoading: isHeaderLoading } =
    useQuery({
      queryKey: ['expenseClaimDetailsHeader', claimId],
      queryFn: () =>
        fetchExpenseClaimDetails({
          claimNo: claimId,
          listType: 'PROCESS_HEADER',
        }),
      enabled: !!claimId,
    });

  const { data: expenseClaimDetails, isLoading: isDetailLoading } = useQuery({
    queryKey: ['expenseClaimDetails', claimId],
    queryFn: () =>
      fetchExpenseClaimDetails({
        claimNo: claimId,
        listType: 'PROCESS_DETAIL',
      }),
    enabled: !!claimId,
  });

  const { data: loanDetails, isLoading: isLoanLoading } = useQuery({
    queryKey: ['loanDetail', claimId],
    queryFn: () =>
      fetchExpenseClaimDetails({
        claimNo: claimId,
        listType: 'LOAN',
      }),
    enabled: !!claimId,
  });

  const isLoadingAll = isHeaderLoading || isDetailLoading || isLoanLoading;

  // =========================================================

  useEffect(() => {
    if (loanDetails && loanDetails.length > 0) {
      // Compute totat advance recovery
      const totalAdvanceRecovery = loanDetails.reduce(
        (acc, item) => acc + parseFloat(item?.['Claim Recovery'] || 0),
        0,
      );
      setExpenseClaimHeaders(prev => ({
        ...prev,
        advanceRecovery: totalAdvanceRecovery,
      }));
    }
  }, [loanDetails]);

  useEffect(() => {
    const fetchDefaultData = async () => {
      if (expenseClaimDetailsHeader && expenseClaimDetailsHeader?.length > 0) {
        const headerData = expenseClaimDetailsHeader[0];
        setExpenseClaimHeaders(prev => ({
          ...prev,
          claimNo: headerData?.ClaimNo || '',
          claimDate: DateUtil.formatDateObjToHtmlDate(
            new Date(headerData?.ClaimDt),
          ),
          currency: headerData?.['Claim Curr']?.trim() || 'AED',
          claimStatus: headerData?.ClaimStatus || '',
          costCenter: headerData?.['Cost Centre']?.trim() || '',
          department: headerData?.Department?.trim() || '',
          employeeId: headerData?.EmployeeCode?.trim() || '',
          employee: headerData?.Employee?.trim() || '',
          claimCurr: headerData?.['Claim Curr']?.trim() || 'AED',
          finCurr: headerData?.FinCurr?.trim() || 'AED',
          finCoCd: headerData?.FinCoCd?.trim() || '',
        }));

        // Get default expense claim data
        const defaultExpenseResponse =
          await fetchDefaultExpenseClaimData.mutateAsync({
            defaultType: 'INFO',
            employeeId: headerData?.EmployeeCode?.trim() || '',
          });
        setDefaultExpenseData(defaultExpenseResponse);
      }
    };
    fetchDefaultData();
  }, [expenseClaimDetailsHeader]);

  const mapExpenseClaimDetails = details =>
    details.map(detail => ({
      expCategory: detail?.ExpenseCatgory?.trim() || '',
      expType: detail?.['Expense Code']?.trim() || '',
      expDate: DateUtil.formatDateObjToHtmlDate(new Date(detail?.ExpenseDate)),
      currency: detail?.ExpenseCurr?.trim() || '',
      amount: parseFloat(detail?.Amt) || 0,
      expRate: parseFloat(detail?.ExRate).toFixed(6).toString() || 0,
      taxAmount: parseFloat(detail?.TaxAmt) || 0,
      exptaxRecover: detail?.TaxRecoverable?.trim() || 'N',
      reimburAmt: parseFloat(detail?.ReimburseAmt) || 0,
      remarks: detail?.Remarks || '',
      acctCode: detail?.AcCd?.trim() || '',
      baseRate: detail?.BaseRate
        ? parseFloat(detail?.BaseRate).toFixed(6).toString()
        : parseFloat(detail?.ExRate).toFixed(6).toString(),
      baseAmt: parseFloat(detail?.BaseAmt) || 0,
      // Modal fields
      entity: detail?.Entity?.trim() || '',
      costCenter: detail?.CostCentre?.trim() || '',
      product: detail?.Product?.trim() || '',
      sorNo: detail?.SORNo?.trim() || '',
      costType: detail?.CostTyp?.trim() || '',
      sMan: detail?.SMan?.trim() || '',
      // orderNo: detail?.OrderNo?.trim() || '',
      // regionCode: detail?.RegionCode?.trim() || '',
      countryCode: detail?.Country?.trim() || '',
      reference: detail?.Reference?.trim() || '',
      expCostAlloc: detail?.CostAllocation?.trim() || 'C00000',
      financeRemarks: detail?.FinRemarks || '',

      // Others fields
      dependent: Number(detail?.Dependent),
      expFromDate:
        detail?.ExpenseFromPrd === '1900-01-01T00:00:00.000Z'
          ? ''
          : DateUtil.formatDateObjToHtmlDate(
              new Date(detail?.ExpenseFromPrd),
            ) || '',
      expToDate:
        detail?.ExpenseToPrd === '1900-01-01T00:00:00.000Z'
          ? ''
          : DateUtil.formatDateObjToHtmlDate(new Date(detail?.ExpenseToPrd)) ||
            '',

      // Additional fields
      srNo: detail?.SrNo || '',
      count: detail?.Count || 0,
    }));

  // =========================================================
  const formMethods = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues,
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const formFieldArray = useFieldArray({
    control: formMethods.control,
    name: 'claims',
  });

  // Reinitialize modal state for a row index
  const resetRowModalState = useCallback(
    rowIndex => {
      Object.entries(modalFieldInitialValues).forEach(([key, value]) => {
        formMethods.setValue(`claims.${rowIndex}.${key}`, value);
      });
    },
    [formMethods, modalFieldInitialValues],
  );

  // Get Row Data
  const getRowData = useCallback(
    rowIndex => formMethods.getValues(`claims.${rowIndex}`) || {},
    [formMethods],
  );

  // Add Row
  const addRow = useCallback(() => {
    if (!sortFilterState) {
      formFieldArray.append({
        ...initialValues,
        currency: expenseClaimHeaders?.claimCurr || '',
      });
    }
  }, [formFieldArray, expenseClaimHeaders?.claimCurr]);

  // Initialize header data
  useEffect(() => {
    if (!defaultCurrCode) return;
    setExpenseClaimHeaders(prev => ({
      ...prev,
      currency: expenseClaimHeaders?.claimCurr,
    }));
  }, [expenseClaimHeaders?.claimCurr]);

  // Compute reimbursement amount for all rows
  const watchedClaims = useWatch({
    control: formMethods.control,
    name: 'claims',
    defaultValue: [],
  });

  useEffect(() => {
    const claims = formMethods.watch('claims') || [];
    let totalClaimAmt = 0;
    claims.forEach(claim => {
      const amtRow =
        parseFloat(claim?.amount) * parseFloat(claim?.expRate) || 0;
      totalClaimAmt += amtRow;
    });
    setExpenseClaimHeaders(prev => ({
      ...prev,
      reimburAmt:
        totalClaimAmt -
        (typeof prev?.advanceRecovery === 'number' ? prev.advanceRecovery : 0),
      claimAmt: totalClaimAmt,
    }));
  }, [
    watchedClaims,
    loanDetails,
    expenseClaimDetails,
    expenseClaimDetailsHeader,
  ]);

  const resetExpenseClaimAll = useCallback(() => {
    formFieldArray.replace(
      Array.from({ length: 1 }, () => ({
        ...initialValues,
        currency: expenseClaimHeaders?.claimCurr || '',
      })),
    );
    setExpenseClaimHeaders({
      claimNo: '',
      claimDate: DateUtil.formatDateObjToHtmlDate(new Date(endDate)),
      currency: defaultCurrCode || 'AED',
      claimAmt: 0,
      reimburAmt: 0,
      advanceRecovery: 0,
      comments: '',
    });
  }, [
    formFieldArray,
    defaultCurrCode,
    setExpenseClaimHeaders,
    endDate,
    expenseClaimHeaders?.claimCurr,
  ]);

  const resetExpenseClaim = () => {
    formFieldArray.replace(
      Array.from({ length: 1 }, () => ({
        ...initialValues,
        currency: expenseClaimHeaders?.claimCurr || '',
      })),
    );
  };

  useEffect(() => {
    if (expenseClaimDetails && expenseClaimDetails.length > 0) {
      setSortFilterState(false);
      let sortFilterFlag = false;
      const mappedDetails = mapExpenseClaimDetails(expenseClaimDetails);
      let filterSortedData = mappedDetails;

      // Filter the data based on pageRequest filters
      if (pageRequest.filters && pageRequest.filters.length > 0) {
        filterSortedData = TableUtil.filterData(
          mappedDetails,
          pageRequest.filters,
        );
        sortFilterFlag = true;
        setSortFilterState(true);
      }

      // Sort the data if sortColumn and sortDirection are provided
      if (pageRequest.sortColumn && pageRequest.sortDirection) {
        filterSortedData = TableUtil.sortArrayByColumn(
          filterSortedData,
          pageRequest.sortColumn,
          pageRequest.sortDirection,
          processClaimSortConfig,
        );
        sortFilterFlag = true;
        setSortFilterState(true);
      }

      if (!sortFilterFlag) {
        setOldResult(JSON.parse(JSON.stringify(filterSortedData)));
        filterSortedData.push({
          ...initialValues,
          currency: expenseClaimHeaders?.claimCurr || '',
        });
      } else {
        setOldResult(JSON.parse(JSON.stringify(filterSortedData)));
      }

      // Set a set of old result to compare with new result later
      formFieldArray.replace(filterSortedData);
    } else {
      resetExpenseClaim();
    }
  }, [
    expenseClaimDetails,
    pageRequest,
    expenseCategories,
    expenseClaimHeaders?.claimCurr,
  ]);

  const getColumnData = useCallback(
    colName => TableUtil.getUniqueColumnValues(oldResult, colName),
    [oldResult],
  );

  /**
   * Compare two arrays of row‐objects as strings and return a change log,
   * including “added” rows for any new entries.
   *
   * @param {Object[]} oldData
   * @param {Object[]} newData
   * @param {{key:string,label:string}[]} config
   * @returns {string}
   */
  async function generateChangeLog(oldData, newData, config) {
    const linePromises = newData.map(async (newRow, rowIndex) => {
      if (rowIndex < oldData.length) {
        const oldRow = oldData[rowIndex] || {};
        const diffPromises = config.map(({ key, label }) => {
          const oldStr = String(oldRow[key] ?? '').trim();
          const newStr = String(newRow[key] ?? '').trim();
          if (oldStr === newStr) return null;
          if (key === 'expRate') {
            // make both parsefloat and to fixed 6 and compare
            const oldRate = parseFloat(oldStr).toFixed(6);
            const newRate = parseFloat(newStr).toFixed(6);
            if (oldRate === newRate) return null;
            return Promise.resolve(`${label}: ${oldRate} ---> ${newRate}`);
          }
          if (key === 'baseRate') {
            // make both parsefloat and to fixed 6 and compare
            const oldRate = parseFloat(oldStr).toFixed(6);
            const newRate = parseFloat(newStr).toFixed(6);
            if (oldRate === newRate) return null;
            return Promise.resolve(`${label}: ${oldRate} ---> ${newRate}`);
          }
          if (key === 'expCategory') {
            const oldCategory = expenseCategories.find(
              c => c.Code === oldStr,
            )?.Description;
            const newCategory = expenseCategories.find(
              c => c.Code === newStr,
            )?.Description;
            return Promise.resolve(
              `${label}: ${oldCategory || oldStr} ---> ${newCategory || newStr}`,
            );
          }
          if (key === 'expType') {
            return Promise.all([
              fetchCodeListMutation.mutateAsync({
                codeType: 'EXPENSE_TYPE',
                param1: oldRow.expCategory,
              }),
              fetchCodeListMutation.mutateAsync({
                codeType: 'EXPENSE_TYPE',
                param1: newRow.expCategory,
              }),
            ]).then(([oldList, newList]) => {
              const oldType = oldList.find(t => t.Code === oldStr)?.Description;
              const newType = newList.find(t => t.Code === newStr)?.Description;
              return `${label}: ${oldType || oldStr} ---> ${newType || newStr}`;
            });
          }
          return Promise.resolve(`${label}: ${oldStr} ---> ${newStr}`);
        });

        // Wait for all diffs to resolve and filter out nulls
        const diffs = (await Promise.all(diffPromises)).filter(Boolean);
        return diffs.length
          ? `Line No ${rowIndex + 1}: ${diffs.join(', ')}`
          : null;
      }
      const parts = await config.reduce(async (accPromise, { key, label }) => {
        const acc = await accPromise;
        const raw = newRow[key];
        if (raw != null) {
          if (key === 'expCategory') {
            const category = expenseCategories.find(
              c => c.Code === raw,
            )?.Description;
            if (category) {
              acc.push(`${label}: ${category}`);
            }
          } else if (key === 'expType') {
            const typeList = await fetchCodeListMutation.mutateAsync({
              codeType: 'EXPENSE_TYPE',
              param1: newRow.expCategory,
            });
            const type = typeList.find(t => t.Code === raw)?.Description;
            if (type) {
              acc.push(`${label}: ${type}`);
            }
          } else {
            const v = String(raw).trim();
            if (v) acc.push(`${label}: ${v}`);
          }
        }
        return acc;
      }, Promise.resolve([]));

      return parts.length
        ? `Added new Expense Claim: ${parts.join(', ')}`
        : null;
    });
    const lines = (await Promise.all(linePromises)).filter(Boolean);
    return lines.join(';\n\n');
  }

  function getRealFormValues() {
    return new Promise(resolve => {
      formMethods.handleSubmit(values => {
        resolve(values);
      })();
    });
  }

  // new memoized version:
  const generateChangeLogForExpenseClaims = useCallback(async () => {
    const oldData = oldResult || [];
    const newClaims = await getRealFormValues();
    return generateChangeLog(oldData, newClaims?.claims, [
      ...processClaimSortConfig,
      ...processClaimModalSortConfig,
    ]);
  }, [oldResult]);

  /**
   * @param {boolean} focus - if true, scroll/focus the first invalid field
   * @returns {Promise<boolean>} whether validation errors were found
   */
  const runValidationsAndToastErrors = useCallback(
    async (focus = false) => {
      await formMethods.trigger();
      const { errors } = formMethods.formState;
      const claimErrors = errors.claims;
      let hasErrors = false;

      if (Array.isArray(claimErrors)) {
        const rowMessages = claimErrors
          .map((rowError, idx) => {
            if (!rowError) return null;
            hasErrors = true;
            const msgs = Object.values(rowError).map(
              e => e?.message || 'Invalid',
            );
            return `Row ${idx + 1}: ${msgs.join('; ')}`;
          })
          .filter(Boolean);

        if (rowMessages.length) {
          toast.error(rowMessages.join('\n'), {
            className: 'toast-error',
          });
        }
      }

      if (hasErrors && focus) {
        // scroll & focus the first invalid input
        const el = document.querySelector('.is-invalid');
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.focus();
        }
      }

      return hasErrors;
    },
    [formMethods],
  );

  useEffect(() => {
    if (expenseCategories && expenseCategories.length > 0) {
      const mapping = {};
      expenseCategories.forEach(cat => {
        mapping[cat.Code] = cat.Description;
      });
      setExpenseCatMapping(mapping);
    }
  }, [expenseCategories]);

  const validateLastRow = useCallback(
    /**
     * @param {boolean} focus
     * @param {boolean} showToast
     * @returns {Promise<{ isValid: boolean; errors: Record<string,string> }>}
     */
    async (focus = false, showToast = true) => {
      const claims = formMethods.getValues('claims') || [];
      const lastIndex = claims.length - 1;
      if (lastIndex < 0) {
        return { isValid: true, errors: {} };
      }

      // clear any old errors on that last row
      formMethods.clearErrors(`claims.${lastIndex}`);

      try {
        // validate only that row
        await rowSchema.validate(claims[lastIndex], { abortEarly: false });
        return { isValid: true, errors: {} };
      } catch (validationError) {
        // build a map of field → message
        const errorMap = {};
        validationError.inner.forEach(e => {
          errorMap[e.path] = e.message;
          // register with RHF so .is-invalid shows up
          formMethods.setError(`claims.${lastIndex}.${e.path}`, {
            type: 'manual',
            message: e.message,
          });
        });

        // toast a summary if desired
        if (showToast) {
          const msgs = Object.values(errorMap).join('; ');
          toast.error(`Row ${lastIndex + 1}: ${msgs}`, {
            className: 'toast-error',
          });
        }

        // optionally scroll & focus
        if (focus) {
          const el = document.querySelector('.is-invalid');
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            el.focus();
          }
        }

        return { isValid: false, errors: errorMap };
      }
    },
    [formMethods],
  );

  const value = useMemo(
    () => ({
      formMethods,
      formFieldArray,
      resetRowModalState,
      addRow,
      getRowData,
      expenseClaimHeaders,
      setExpenseClaimHeaders,
      getExpenseClaimDetailsMutation,
      isModalOpen,
      setIsModalOpen,
      modalConfig,
      setModalConfig,
      resetExpenseClaimAll,
      setViewAttachmentsModalOpen,
      viewAttachmentsModalOpen,
      viewAttachmentSrNo,
      setViewAttachmentSrNo,
      applyFilter,
      handleManualSort,
      resetFilters,
      checkColumnIfSorted,
      columnName,
      setColumnName,
      getColumnData,
      pageRequest,
      setPageRequest,
      generateChangeLogForExpenseClaims,
      runValidationsAndToastErrors,
      clearSingleFilterValue,
      expenseCatMapping,
      setExpenseCatMapping,
      expenseTypeMapping,
      setExpenseTypeMapping,
      validateLastRow,
      isLoadingAll,
      handleAutoSort,
      defaultExpenseData,
    }),
    [
      formMethods,
      formFieldArray,
      resetRowModalState,
      addRow,
      getRowData,
      expenseClaimHeaders,
      setExpenseClaimHeaders,
      getExpenseClaimDetailsMutation,
      isModalOpen,
      setIsModalOpen,
      modalConfig,
      setModalConfig,
      resetExpenseClaimAll,
      setViewAttachmentsModalOpen,
      viewAttachmentsModalOpen,
      viewAttachmentSrNo,
      setViewAttachmentSrNo,
      applyFilter,
      handleManualSort,
      resetFilters,
      checkColumnIfSorted,
      columnName,
      setColumnName,
      getColumnData,
      pageRequest,
      setPageRequest,
      generateChangeLogForExpenseClaims,
      runValidationsAndToastErrors,
      clearSingleFilterValue,
      expenseCatMapping,
      setExpenseCatMapping,
      expenseTypeMapping,
      setExpenseTypeMapping,
      validateLastRow,
      isLoadingAll,
      handleAutoSort,
      defaultExpenseData,
    ],
  );

  return (
    <ProcessExpenseClaimContext.Provider value={value}>
      {children}
    </ProcessExpenseClaimContext.Provider>
  );
}
